<template>
    <div>
        <ais-instant-search
            :key="sessionStore.index"
            :search-client="searchClient"
            :index-name="sessionStore.index"
            :future="future"
            :on-state-change="onStateChange"
            :routing="routing"
            :insights="true"
            :middlewares="middlewares"
            @search="{ state }"
        >
            <ais-configure
                :hits-per-page.camel="12"
                :filters="filters"
                :click-analytics.camel="true"
                :user-token.camel="userToken" />
            <div class="search-panel">
                <div class="search-panel__results">
                    <div class="algolia-search-form"  :class="{ 'department-search': isDepartment }">
                        <div class="search-panel-box" :class="{ 'department-search-container flex-row': isDepartment }">
                            <div v-if="isDepartment" class="department-search-image flex-col">
                                <img :src="getDeptImage(selectedDepartment)" :alt="getDeptName(selectedDepartment)" />
                            </div>
                            <div v-if="isDepartment" class="department-search-col flex-col">
                                <h1>{{ getDeptName(selectedDepartment) }}</h1>
                            </div>

                            <div :class="{'department-search-row flex-row': isDepartment}">
                                <div class="searchbox">
                                    <ais-search-box 
                                        v-model="searchQuery" 
                                        :placeholder="isDepartment ? `Search ${getDeptName(selectedDepartment)}` : 'Keywords, Item #, or UPC #'"
                                        :show-loading-indicator="false"
                                        @keydown.native="handleEnterKey"
                                    />
                                </div>
                            </div>

                            <div class="search-panel__filters flex-row" :class="{'is-hidden': isDepartment}">
                                <!-- Department Filter -->
                                <!--- Transforming items caused issues. Leaving this in comments for future use. -->
                                <!-- :transform-items="(items) => items.sort((a, b) => getDeptName(a.label).localeCompare(getDeptName(b.label)))" -->
                                <ais-refinement-list attribute="dept" 
                                    :limit="50" 
                                    :show-more="true" 
                                    :show-more-limit="100"
                                >
                                    <template v-slot="{ items, refine }">
                                        <div class="dropdown-filter">
                                            <select v-model="selectedDepartment" @change="() => onDepartmentChange(refine, items)" :class="{'is-placeholder': selectedDepartment == ''}">
                                                <option value="" selected>All Departments</option>
                                                <option v-for="dept in items" :key="dept.label" :value="dept.value">
                                                    {{ getDeptName(dept.label) }} ({{ dept.count }})
                                                </option>
                                            </select>
                                        </div>
                                    </template>
                                </ais-refinement-list>

                                <!-- Price Type Filter -->
                                <ais-refinement-list :attribute="ptypeKey">
                                    <template v-slot="{ items, refine }">
                                        <div class="dropdown-filter">
                                            <select v-model="selectedPriceType" @change="() => onTypeChange(refine, items)" :class="{'is-placeholder': selectedPriceType == ''}">
                                                <option value="" selected>All Products</option>
                                                <option v-for="pType in items" :key="pType.label" :value="pType.value">
                                                    {{ getTypeName(pType.label) }} ({{ pType.count }})
                                                </option>
                                            </select>
                                        </div>
                                    </template>
                                </ais-refinement-list>
                            </div>
                        </div>
                    </div>

                    <!-- Show stats if a query exists (even if no results are found) -->
                    <div v-if="showResults" class="search-panel__stats">
                        <ais-stats>
                            <template v-slot="{ nbHits, query }">
                                Found <b>{{ nbHits }}</b> {{ nbHits === 1 ? 'product' : 'products' }} that match your search
                                <span v-if="searchQuery.trim().length >= 3"> for <span class="keywords">"{{ query }}"</span></span>
                                <span v-if="selectedPriceType || selectedDepartment"> in </span>
                                <span v-if="selectedPriceType">{{  getTypeName(selectedPriceType) }}</span>
                                <span v-if="selectedPriceType && selectedDepartment"> and </span>
                                <span v-if="selectedDepartment"> the {{  getDeptName(selectedDepartment) }} department</span>
                                <span v-if="selectedPriceType || selectedDepartment">.</span>
                            </template>
                        </ais-stats>
                    </div>

                   <app-infinite-hits 
                        v-slot="{ item, state }"
                        :query="searchQuery"
                        :filters="filters"
                        @update:item-count="displayedCount = $event"
                    >
                        <SearchProductTile
                            v-if="showResults"
                            :item="item"
                            :key="item.objectID"
                            :state="state"
                            :query="searchQuery"
                            :filters="filters"
                            :itemDesc="getItemDesc(item)"
                            :data-insights-object-id="item.objectID"
                            :data-insights-position="item.__position"
                            :data-insights-query-id="item.__queryID" />
                    </app-infinite-hits>

                    <div v-if="showResults" class="search-panel__stats">
                        <ais-stats>
                            <template v-slot="{ hitsPerPage, nbHits, page }">
                                <p class="product-count center marginTop25 mobile-align-left">
                                    {{ displayedCount }} of {{ nbHits }}
                                </p>
                            </template>
                        </ais-stats>
                    </div>
                </div>
            </div>
        </ais-instant-search>
    </div>
</template>

<script setup>
    import { liteClient as algoliasearch } from 'algoliasearch/lite';
    import AppInfiniteHits from '@/components/InfiniteHits.vue';
    import SearchProductTile from '@/components/SearchProduct/SearchProductTile.vue';
    import { history } from 'instantsearch.js/es/lib/routers';
    import { createInsightsMiddleware } from 'instantsearch.js/es/middlewares';
    import { useSessionStore } from "@/store/storeSession.js";
    import { computed, inject, ref } from 'vue';
    import { storeToRefs } from 'pinia';
    import { debounce } from 'lodash';
    import { aaClient, initAlgoliaInsights } from "@/utils/algoliaInsights.js";
    import { sanitizeForUrl } from '@/utils/algoliaSearch.js';

    const sessionStore = useSessionStore();
    sessionStore.getUserStore();
    sessionStore.getDepartments();
    sessionStore.getExpiredItems();
    
    const entryAppId = inject('global_appId');
    const entryApiKey = inject('global_apiKey');
    const entryAppIdStag = inject('global_appIdStag');
    const entryApiKeyStag = inject('global_apiKeyStag');
    const entryEnvironment = inject('global_environment');
    const entryIndex = inject('global_index');
    const entryIndexStag = inject('global_indexStag');
    const entryStore = inject('global_store');
    const entryIsAvibe = inject('global_isAvibe');
    const entryTrackAlgolia = inject('global_trackAlgolia');
    const entryImgix = inject('global_imgix');
    const entryUserToken = inject('global_userToken');
    const entryAuthUserToken = inject('global_authUserToken');
    const entryHasStore = inject('global_hasStore');

    const { appId, apiKey, appIdStag, apiKeyStag, environment, index, indexStag, storeNum, isAvibe, trackAlgolia, imgix, userToken, authUserToken, hasStore } = storeToRefs(sessionStore);

    appId.value = entryAppId;
    apiKey.value = entryApiKey;
    appIdStag.value = entryAppIdStag;
    apiKeyStag.value = entryApiKeyStag;
    environment.value = entryEnvironment;
    index.value = entryIndex;
    indexStag.value = entryIndexStag;
    storeNum.value = entryStore;
    isAvibe.value = entryIsAvibe;
    trackAlgolia.value = entryTrackAlgolia;
    imgix.value = entryImgix;
    userToken.value = entryUserToken;
    authUserToken.value = entryAuthUserToken;
    hasStore.value = entryHasStore;

    initAlgoliaInsights(sessionStore);

    const algoliaClient = algoliasearch(
        appId.value,
        apiKey.value
    );

    const showResults = computed(() => {
        return searchQuery.value.trim().length >= 3 || 
            selectedCategory.value || 
            selectedDepartment.value || 
            selectedPriceType.value;
    });

    const debouncedSearch = debounce((requests, resolve, reject) => {
        const modifiedRequests = requests.map(request => ({
            ...request,
            params: {
                ...request.params,
                clickAnalytics: true,
            }
        }));

        algoliaClient
            .search(modifiedRequests)
            .then((result)=> {
                resolve(result);
            })
            .catch(reject);
    }, 200);

    const searchClient = {
        ...algoliaClient,
        search(requests) {
            // if (requests.every(({ params }) => ((!params.query || params.query.length < 3) && !selectedCategory.value && !selectedDepartment.value && !selectedPriceType.value))) {
            //     return Promise.resolve({
            //         results: requests.map(() => ({
            //             hits: [],
            //             nbHits: 0,
            //             nbPages: 0,
            //             page: 0,
            //             processingTimeMS: 0,
            //             hitsPerPage: 0,
            //             exhaustiveNbHits: false,
            //             query: "",
            //             params: "",
            //         })),
            //     });
            // }

            return new Promise((resolve, reject) => {
                debouncedSearch(requests, resolve, reject);
            });
        },
    };

    const future = { preserveSharedStateOnUnmount: true };
    const selectedCategory = ref('');
    const selectedDepartment = ref('');
    const selectedPriceType = ref('');
    const searchQuery = ref('');
    const isDepartment = ref(false);
    const fullSearchPath = '/search/fullsearch/';
    const newSearchPath = '/search/newproducts/';
    const tprSearchPath = '/search/temporarypricereductions/';

    const filters = computed(() => {
        let filters = `storeNum=${sessionStore.storeNum}`; // Use = for numeric, not :
        if (selectedCategory.value) {
            filters += ` AND cat:"${selectedCategory.value}"`;
        }
        return filters;
    });

    const ptypeKey = computed(() => `ptype.${sessionStore.storeNum}`);

    const insightsMiddleware = createInsightsMiddleware({
        aa: aaClient,
        onEvent(event) {
            if (!trackAlgolia.value) return;
            
            const { widgetType, eventType, payload, hits } = event;

            if (widgetType === 'ais.hits' && eventType === 'view') {
                dataLayer.push({ event: 'Hits Viewed' });
            }
        }
    });
    const middlewares = ref([insightsMiddleware]);

    const getItemDesc = (item) => {
        const descNum = item.itemDescStore[sessionStore.storeNum];
        if (descNum === 1) return item.itemDesc1;
        if (descNum === 2) return item.itemDesc2;
        return item.itemDesc1;
    };

    const isEncoded = (str) => {
        try {
            return str !== decodeURIComponent(str);
        } catch {
            return false;
        }
    };

    const decodeURL = (str) => {
        if (!str) return '';
        try {
            let result = str;
            while (isEncoded(result)) {
                result = decodeURIComponent(result);
            }
            return result.replace(/-/g, ' ').replace(/_/g, '/');
        } catch (e) {
            return str;
        }
    };

    const routing = {
        router: history({
            cleanUrlOnDispose: true,
            createURL({ qsModule, routeState, location }) {
                const pathData = getPathData(location);
                const urlParts = location.href.match(new RegExp(`^(.*?)${pathData.basePath}`));
                const baseUrl = `${urlParts ? urlParts[1] : ''}`;
                const queryParameters = {};
                let queryPath = '';

                if (routeState.query && routeState.query.length >= 3) {
                    queryPath = sanitizeForUrl(routeState.query) + '/';
                }
                if (routeState.cat && routeState.cat.length >= 3) {
                    queryPath = sanitizeForUrl(routeState.cat) + '/';
                }
                if (pathData.department.length <= 0 && routeState.dept && routeState.dept.length > 0) {
                    queryParameters.dept = routeState.dept.map(encodeURIComponent);
                }
                if (routeState[ptypeKey.value] && routeState[ptypeKey.value].length > 0) {
                    if(routeState[ptypeKey.value].includes('new')) {
                        pathData.basePath = newSearchPath;
                    } else if(routeState[ptypeKey.value].includes('tpr')) {
                        pathData.basePath = tprSearchPath;
                    } else {
                        pathData.basePath = fullSearchPath;
                    }
                } else if (pathData.department.length <= 0) {
                    pathData.basePath = fullSearchPath;
                }

                let queryString = qsModule.stringify(queryParameters, {
                    addQueryPrefix: true,
                    arrayFormat: 'repeat',
                });

                return `${baseUrl}${pathData.basePath}${queryPath}${queryString}`;
            },
            parseURL({ qsModule, location }) {
                let pathData = getPathData(location);
                const qs = qsModule.parse(location.search.slice(1));
                const ptypeValue = pathData.priceType || qs[ptypeKey.value] || [];
                let { 
                    query = pathData.query, 
                    cat = '', 
                    dept = pathData.department || qsModule.dept
                } = qs;

                // filters do not return an array when there's a single value.
                let allDepts = Array.isArray(dept)
                    ? dept
                    : [dept].filter(Boolean);
                let allPTypes = Array.isArray(ptypeValue)
                    ? ptypeValue
                    : (ptypeValue ? [ptypeValue] : []);

                searchQuery.value = query;
                selectedCategory.value = cat;
                selectedDepartment.value = allDepts[0] || '';
                selectedPriceType.value = allPTypes[0] || '';

                return {
                    query: decodeURL(query),
                    cat: decodeURL(cat),
                    dept: allDepts.map(decodeURIComponent),
                    [ptypeKey.value]: allPTypes.map(decodeURIComponent),
                };
            },
        }),
        stateMapping: {
            stateToRoute(uiState) {
                const index = sessionStore.index || "";
                const indexUiState = uiState[index] || {};

                return {
                    query: decodeURL(indexUiState.query) || '',
                    cat: indexUiState.cat || '',
                    dept: indexUiState.refinementList?.dept || [],
                    [ptypeKey.value]: indexUiState.refinementList?.[ptypeKey.value] || []
                };
            },
            routeToState(routeState) {
                const index = sessionStore.index || "";

                return {
                    [index]: {
                        query: decodeURL(routeState.query) || '',
                        cat: routeState.cat || '',
                        refinementList: {
                            dept: routeState.dept || [],
                            [ptypeKey.value]: routeState[ptypeKey.value] || [],
                        },
                    },
                };
            }
        },
    };

    const onStateChange = debounce(({ uiState, setUiState }) => {
        setTitleHeight('.product-tile-title');
        sessionStore.getUserFavorites();
        sessionStore.getUserCartItems();
        selectedCategory.value = '';
        setUiState(uiState);
        clearSearchState();
    }, 100);

    function getPathData(location) {
        let pathSections = location.pathname.split('/').filter(section => section.length > 0);
        let pathLength = pathSections.length;

        let results = [];
            results.pathLength = pathLength;
            results.basePath = '';
            results.query = '';
            results.department = [];
            results.priceType = [];

        let isFullSearch = location.href.includes(fullSearchPath);
        let isNewSearch = location.href.includes(newSearchPath);
        let isTprSearch = location.href.includes(tprSearchPath);
        let isDeptSearch = false;

        if(isFullSearch) {
            results.basePath = fullSearchPath;
        } else if(isNewSearch) {
            results.basePath = newSearchPath;
            results.priceType.push('new');
        } else if(isTprSearch) {
            results.basePath = tprSearchPath;
            results.priceType.push('tpr');
        } else if(location.href.includes('/search/products/')) {
            // To do - EB and Promo Items
            results.basePath = '/search/products/';
        } else if(pathLength > 1) {
            results.basePath = `/search/${pathSections[1]}/`;
            results.department.push(pathSections[1]);

            isDepartment.value = true;
            isDeptSearch = true;
        }

        if((isFullSearch || isNewSearch || isTprSearch || isDeptSearch) && pathLength > 2) {
            results.query = decodeURL(pathSections[2]);
        }

        return results;
    }

    function onDepartmentChange(refine, items) {
        // Reset previous filters
        items.forEach(item => {
            if (item.isRefined) {
                refine(item.value); 
            }
        });

        // Set filter
        if (selectedDepartment.value) refine(selectedDepartment.value);
    }

    function onTypeChange(refine, items) {
        // Reset previous filters
        items.forEach(item => {
            if (item.isRefined) {
                refine(item.value);
            }
        });

        // Set filter
        if (selectedPriceType.value) refine(selectedPriceType.value);
    }

    function getDeptImage(dept) {
        let deptImage = sessionStore.departments[dept] ? `/images/department/${sessionStore.departments[dept].image}` : '';

        return deptImage;
    }

    function getDeptName(dept) {
        let deptName = sessionStore.departments[dept] ? sessionStore.departments[dept].deptName : '';

        return deptName;
    }

    function getTypeName(ptype) {
        if(ptype == 'new') return 'New Products';
        else if(ptype == 'tpr') return 'Temporary Price Reductions';
        else return '';
    }

    // function getItemCount(hitsPerPage, nbHits, page) {
    //     let itemCount = (page + 1) * hitsPerPage;

    //     if(itemCount > nbHits) itemCount = nbHits;

    //     return itemCount;
    // }

    function handleEnterKey(event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.target.blur();
        }
    }

    function clearSearchState() {
        sessionStore.setSearchState();
    }
</script>

<style>
    .pSearchForm {
        border: none !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    #app input::-webkit-input-placeholder
    {
        color: #757575;
    }

    #app .search-panel-box:not(.department-search-container) {
        border: 1px solid #ccc;
        margin-top: 5px;
        padding: 20px 35px 18px 35px;
    }

    .pSearchForm.department-search {
        background-color: transparent !important;
        border-radius: 0 !important;
    }

    #app .search-panel__filters {
        justify-content: space-between;
        gap: 20px;
        width: 100%;
    }

    #app .ais-RefinementList,
    #app .ais-RefinementList .dropdown-filter {
        width: 100%;
    }

    #app .search-panel__stats {
        margin-top: 22px;
    }

    #app {
        width: 100%;
    }

    #app ol.ais-Hits-list {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        left: -10px;
        list-style-type: none;
        padding: 0;
        margin: 10px 0 0 0;
        position: relative;
        /* row-gap: 20px; */
        width: calc(100% + 20px);
    }

    #app .product-tile {
        height: calc(100% - 20px);
    }

    #app .is-svg,
    #app .column img.is-svg {
        max-width: 175px;
        width: 100%;
    }

    #app .ais-Hits--empty
    {
        display: none;
    }

    #app .searchbox 
    {
        width: 100%;
    }

    #app .ais-SearchBox-form
    {
        display: flex;
        flex-direction: row;
        gap: 10px;
        position: relative;
    }

    #app .ais-SearchBox-input
    {
        margin: 0 !important;
    }

    #app input[type='search'] {
        font-size: 18px;
        margin-top: 0;
        padding-left: 15px;
        padding-right: 65px;
        transition: box-shadow 0.3s;
    }

    #app .department-search-container input[type='search'] 
    {
        height: 60px;
    }

    #app .ais-SearchBox-form button
    {
        background-color: transparent;
        border: 1px solid transparent;
        border-left: 1px solid #999;
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
        cursor: pointer;
        height: 40px;
        overflow: hidden;
        padding: 1px 6px;
        position: absolute;
        right: 0;
        top: 0;
        width: 40px;
        transition: all 0.3s;
    }

    #app .department-search-container .ais-SearchBox-form button
    {
        height: 60px;
        width: 60px;
    }

    #app .ais-SearchBox-form button.ais-SearchBox-reset
    {
        border: none;
        border-radius: 5px;
        height: 30px;
        padding: 1px;
        right: 50px;
        top: 5px;
        width: 32px;
    }

    #app .department-search-container .ais-SearchBox-form button.ais-SearchBox-reset
    {
        height: 40px;
        right: 70px;
        top: 10px;
        width: 42px;
    }

    #app .ais-SearchBox-form button svg
    {
        fill: #333;
        height: 25px;
        max-height: 100%;
        width: 25px;
        transition: all 0.3s;
        transform: scale(0.8);
    }

    #app .ais-SearchBox-form button.ais-SearchBox-reset svg 
    {
        height: 20px;
        width: 20px;
    }

    #app .department-search-container .ais-SearchBox-form button svg 
    {
        height: 30px;
        width: 30px;
    }

    #app .department-search-container .ais-SearchBox-form button.ais-SearchBox-reset svg 
    {
        height: 24px;
        width: 24px;
    }

    #app .ais-SearchBox-form button:hover svg,
    #app .ais-SearchBox-form button:focus-visible svg {
        fill: #fff;
        transform: scale(1);
    }

    #app .ais-SearchBox-form button:hover,
    #app .ais-SearchBox-form button:focus-visible {
        background-color: #CF4520;
        background-size: 34px 34px;
        border-color: #CF4520;
    }

    #app #search-clear,
    #app input[type="search" i]::-webkit-search-cancel-button 
    {
        display: none;
    }

    .is-placeholder {
        color: #757575;
    }

    select.is-placeholder:focus,
    select.is-placeholder:focus-visible {
        color: #333;
    }

    @media only screen and (max-width: 900px) {
        #app ol.ais-Hits-list {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    @media only screen and (max-width: 768px) {
        #app .search-panel-box:not(.department-search-container) {
            padding: 20px 20px 18px 20px;
        }
    }

    @media only screen and (max-width: 600px) {
        #app ol.ais-Hits-list {
            grid-template-columns: repeat(1, 1fr);

            left: 0;
            /* margin: 10px 0 0 0;
            row-gap: 20px; */
            width: 100%;
        }

        #app .product-tile {
            height: auto;
        }
    }

    @media only screen and (max-width: 568px) {
        #app .search-panel__filters{
            flex-direction: column;
            gap: 0;
        }
    }
</style>
